import { useQueryClient } from '@tanstack/react-query'
import {
  Text,
  Skeleton,
  FolderIcon,
  Switch,
  showToast,
  Tooltip,
  QuestionFillIcon,
} from '@mercai/clever'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { CardInfo } from '@/components'
import { ROUTES_ADMIN } from '@/router'
import { QUERIES_ADMIN, useUpdateQuotation } from '@/services'

import { formatDate } from '../../show.constants'
import styles from './summary-state-history.module.css'
import type { SummaryStateHistoryProps } from './summary-state-history.type'

const QuotationSummaryStateHistoryDefault = ({
  createdAt,
  orders,
  quotationRequest,
  creatorEmail,
  keyAccountManager,
  disableNotifications,
  uuid,
  acceptorUser,
}: SummaryStateHistoryProps) => {
  const { t } = useTranslation('screenQuotationV2Show')
  const queryClient = useQueryClient()

  const {
    mutateAsync: mutateAsyncUpdateQuotation,
    isPending: isPendingQuotation,
  } = useUpdateQuotation(uuid)

  const handleDisableNotification = async () => {
    const response = await mutateAsyncUpdateQuotation({
      disableNotifications: !disableNotifications,
    })

    if (response.success) {
      showToast({
        variant: 'success',
        title: t('quotationShowSummaryStateHistoryDisableNotificationSuccess'),
      })

      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION.module, uuid],
      })
    } else {
      showToast({
        variant: 'error',
        title: t('quotationShowSummaryStateHistoryDisableNotificationError'),
      })
    }
  }

  return (
    <CardInfo
      icon={<FolderIcon />}
      title={t('quotationShowSummaryStateHistoryTitle')}
    >
      <div className={styles.content}>
        <div className={styles['content-row']}>
          <Tooltip
            text={t(
              'quotationShowSummaryStateHistoryDisabledNotificationDescription',
            )}
            orientation="right"
            widthText="12rem"
          >
            <Text variant="title-base">
              {t('quotationShowSummaryStateHistoryDisableNotification')}
            </Text>
            <QuestionFillIcon className={styles['content-row-info']} />:
          </Tooltip>

          <div className={styles['content-row-switch']}>
            <Switch
              checked={!disableNotifications}
              onChange={handleDisableNotification}
              disabled={isPendingQuotation}
            />
            <Text variant="text-base">
              {disableNotifications
                ? t('quotationShowSummaryDisableNotification')
                : t('quotationShowSummaryEnableNotification')}
            </Text>
          </div>
        </div>

        <div className={styles['content-row']}>
          <Text variant="title-base">
            {t('quotationShowSummaryStateHistoryCreatedAt')}:
          </Text>

          <Text variant="text-base">
            {createdAt ? formatDate(createdAt) : t('quotationShowSummaryEmpty')}
          </Text>
        </div>

        <div className={styles['content-row']}>
          <Text variant="title-base">
            {t('quotationShowSummaryStateHistoryRFQ')}:
          </Text>

          {quotationRequest ? (
            <Link
              to={ROUTES_ADMIN.BUSINESS.RFQ.SHOW.fullPath(
                quotationRequest.uuid,
              )}
              className={styles.link}
              target="_blank"
            >
              {`#${quotationRequest.reference}`}
            </Link>
          ) : (
            <Text>-</Text>
          )}
        </div>

        <div className={styles['content-row']}>
          <Text variant="title-base">
            {t('quotationShowSummaryStateHistoryOrders')}:
          </Text>
          {!!orders && orders?.length > 0 ? (
            orders?.map((order) => (
              <Link
                to={`${ROUTES_ADMIN.ANALYTICS.ORDER_TRACKER.fullPath()}?search=${order.reference}`}
                className={styles.link}
                target="_blank"
                key={order?.uuid}
              >
                {`#${order?.reference}`}
              </Link>
            ))
          ) : (
            <Text>-</Text>
          )}
        </div>

        <div className={styles['content-row']}>
          <Text variant="title-base">
            {t('quotationShowSummaryStateHistoryCreator')}:
          </Text>

          <Text variant="text-base">
            {creatorEmail || t('quotationShowSummaryEmpty')}
          </Text>
        </div>

        <div className={styles['content-row']}>
          <Text variant="title-base">
            {t('quotationShowSummaryStateHistoryAcceptedBy')}:
          </Text>

          <Text variant="text-base">{acceptorUser?.name || '-'}</Text>
        </div>

        <div className={styles['content-row']}>
          <Text variant="title-base">
            {t('quotationShowSummaryStateHistoryKAM')}:
          </Text>

          <Text variant="text-base">
            {keyAccountManager || t('quotationShowSummaryEmpty')}
          </Text>
        </div>
      </div>
    </CardInfo>
  )
}

const QuotationSummaryStateHistoryLoading = () => {
  const { t } = useTranslation('screenQuotationV2Show')
  return (
    <CardInfo
      icon={<FolderIcon />}
      title={t('quotationShowSummaryStateHistoryTitle')}
    >
      <div className={styles.content}>
        <div className={styles['content-row']}>
          <Skeleton height="1rem" width="40%" />
        </div>
        <div className={styles['content-row']}>
          <Skeleton height="1rem" width="50%" />
        </div>
        <div className={styles['content-row']}>
          <Skeleton height="1rem" width="30%" />
        </div>
        <div className={styles['content-row']}>
          <Skeleton height="1rem" width="40%" />
        </div>
      </div>
    </CardInfo>
  )
}

export const QuotationSummaryStateHistory = (
  props: SummaryStateHistoryProps,
) => {
  if (props.isLoading) {
    return <QuotationSummaryStateHistoryLoading />
  }

  return <QuotationSummaryStateHistoryDefault {...props} />
}
